/**
 * Détail d'une séance + éditeur d'exercices — §C (granularité fine).
 *
 * Ouvre une séance et permet de gérer finement ses exercices : ajout manuel
 * (champ libre ou depuis la palette spécialisée du sport — muscu : séries×reps×
 * charge×RPE ; golf : drills de putting ; endurance : distance/durée), édition,
 * suppression, réordonnancement, et « garnir automatiquement » depuis la
 * bibliothèque. Tout généré par le moteur reste éditable ici.
 */
import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded'
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import EditRoundedIcon from '@mui/icons-material/EditRounded'

import { performanceApi } from '../api/performanceApi'
import {
  PERF_ACCENT,
  SESSION_TYPE_LABELS,
  cardSx,
  darkFieldSx,
} from './shared'

// Champs numériques d'un exercice (clé → libellé court).
const NUM_FIELDS = [
  ['target_sets', 'Séries'],
  ['target_reps', 'Répétitions'],
  ['target_weight', 'Charge (kg)'],
  ['target_rpe', 'RPE (1-10)'],
  ['target_distance', 'Distance (m)'],
  ['target_duration_sec', 'Durée (s)'],
  ['rest_seconds', 'Repos (s)'],
]

const EMPTY = {
  name: '',
  target_sets: '',
  target_reps: '',
  target_weight: '',
  target_rpe: '',
  target_distance: '',
  target_duration_sec: '',
  rest_seconds: '',
  notes: '',
}

// Résumé lisible des cibles d'un exercice (« 4 × 8 · 80 kg · RPE 8 »).
function summarize(ex) {
  const parts = []
  if (ex.target_sets && ex.target_reps) parts.push(`${ex.target_sets} × ${ex.target_reps}`)
  else if (ex.target_sets) parts.push(`${ex.target_sets} séries`)
  else if (ex.target_reps) parts.push(`${ex.target_reps} reps`)
  if (ex.target_weight) parts.push(`${ex.target_weight} kg`)
  if (ex.target_rpe) parts.push(`RPE ${ex.target_rpe}`)
  if (ex.target_distance) parts.push(`${ex.target_distance} m`)
  if (ex.target_duration_sec) {
    const m = Math.round(ex.target_duration_sec / 60)
    parts.push(m >= 1 ? `${m} min` : `${ex.target_duration_sec} s`)
  }
  if (ex.rest_seconds) parts.push(`repos ${ex.rest_seconds}s`)
  return parts.join(' · ')
}

const toNum = (v) => (v === '' || v === null || v === undefined ? null : Number(v))

export default function SessionDetailView({ session, open, onClose, onChanged }) {
  const [exercises, setExercises] = useState([])
  const [library, setLibrary] = useState([])
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [editing, setEditing] = useState(null) // {form, id|null}

  const load = async () => {
    if (!session) return
    setLoading(true)
    const fresh = await performanceApi.sessions.get(session.id)
    setExercises(((fresh && fresh.exercises) || []).slice().sort((a, b) => a.order - b.order))
    const lib = await performanceApi.exerciseLibrary(session.sport, session.session_type)
    setLibrary((lib && lib.library) || [])
    setLoading(false)
  }

  useEffect(() => {
    if (open) load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, session && session.id])

  const refresh = async () => {
    await load()
    if (onChanged) onChanged()
  }

  const nextOrder = () =>
    (exercises.reduce((m, e) => Math.max(m, e.order || 0), 0) || 0) + 1

  const addFromLibrary = async (tpl) => {
    setBusy(true)
    try {
      await performanceApi.exercises.create({
        session: session.id,
        order: nextOrder(),
        name: tpl.name,
        target_sets: tpl.target_sets,
        target_reps: tpl.target_reps,
        target_weight: tpl.target_weight,
        target_rpe: tpl.target_rpe,
        target_distance: tpl.target_distance,
        target_duration_sec: tpl.target_duration_sec,
        rest_seconds: tpl.rest_seconds,
        notes: tpl.notes || '',
      })
      await refresh()
    } finally {
      setBusy(false)
    }
  }

  const autoFill = async () => {
    setBusy(true)
    try {
      // replace=true seulement si la séance est vide → sinon on complète.
      await performanceApi.applySessionTemplate(session.id, {
        replace: exercises.length === 0,
      })
      await refresh()
    } finally {
      setBusy(false)
    }
  }

  const saveExercise = async () => {
    if (!editing || !editing.form.name.trim()) return
    setBusy(true)
    const body = {
      name: editing.form.name.trim(),
      target_sets: toNum(editing.form.target_sets),
      target_reps: toNum(editing.form.target_reps),
      target_weight: toNum(editing.form.target_weight),
      target_rpe: toNum(editing.form.target_rpe),
      target_distance: toNum(editing.form.target_distance),
      target_duration_sec: toNum(editing.form.target_duration_sec),
      rest_seconds: toNum(editing.form.rest_seconds),
      notes: editing.form.notes || '',
    }
    try {
      if (editing.id) {
        await performanceApi.exercises.update(editing.id, body)
      } else {
        await performanceApi.exercises.create({
          ...body,
          session: session.id,
          order: nextOrder(),
        })
      }
      setEditing(null)
      await refresh()
    } finally {
      setBusy(false)
    }
  }

  const removeExercise = async (id) => {
    setBusy(true)
    try {
      await performanceApi.exercises.remove(id)
      await refresh()
    } finally {
      setBusy(false)
    }
  }

  // Échange l'ordre avec le voisin (réordonnancement).
  const move = async (idx, dir) => {
    const j = idx + dir
    if (j < 0 || j >= exercises.length) return
    const a = exercises[idx]
    const b = exercises[j]
    setBusy(true)
    try {
      await performanceApi.exercises.update(a.id, { order: b.order })
      await performanceApi.exercises.update(b.id, { order: a.order })
      await refresh()
    } finally {
      setBusy(false)
    }
  }

  if (!session) return null

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { bgcolor: '#11162a', color: '#fff' } }}>
      <DialogTitle sx={{ pb: 0.5 }}>
        <Typography sx={{ fontWeight: 800, fontSize: 20 }}>{session.title}</Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 13 }}>
          {session.scheduled_date}
          {session.sport_name ? ` · ${session.sport_name}` : ''}
          {` · ${SESSION_TYPE_LABELS[session.session_type] || session.session_type}`}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Stack direction="row" spacing={1} sx={{ my: 1.5 }} flexWrap="wrap" useFlexGap>
          <Button
            size="small"
            startIcon={<AutoAwesomeRoundedIcon />}
            onClick={autoFill}
            disabled={busy}
            sx={{ color: PERF_ACCENT, textTransform: 'none' }}
          >
            {exercises.length === 0 ? 'Garnir automatiquement' : 'Compléter depuis la bibliothèque'}
          </Button>
          <Button
            size="small"
            startIcon={<AddRoundedIcon />}
            onClick={() => setEditing({ id: null, form: { ...EMPTY } })}
            disabled={busy}
            sx={{ color: '#fff', textTransform: 'none' }}
          >
            Exercice personnalisé
          </Button>
        </Stack>

        {/* Liste des exercices */}
        {loading ? (
          <Typography sx={{ color: 'rgba(255,255,255,0.5)', py: 2 }}>Chargement…</Typography>
        ) : exercises.length === 0 ? (
          <Box sx={{ ...cardSx, mb: 2 }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.55)' }}>
              Aucun exercice. Garnissez la séance automatiquement, ou ajoutez vos
              exercices depuis la palette ci-dessous.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1} sx={{ mb: 2 }}>
            {exercises.map((ex, idx) => (
              <Box key={ex.id} sx={{ ...cardSx, p: 1.5 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography sx={{ color: PERF_ACCENT, fontWeight: 700, width: 22 }}>
                    {idx + 1}
                  </Typography>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ color: '#fff', fontWeight: 600 }}>{ex.name}</Typography>
                    {summarize(ex) && (
                      <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>
                        {summarize(ex)}
                      </Typography>
                    )}
                    {ex.notes && (
                      <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 12, fontStyle: 'italic' }}>
                        {ex.notes}
                      </Typography>
                    )}
                  </Box>
                  <IconButton size="small" disabled={busy || idx === 0}
                    onClick={() => move(idx, -1)} sx={{ color: 'rgba(255,255,255,0.5)' }}>
                    <ArrowUpwardRoundedIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" disabled={busy || idx === exercises.length - 1}
                    onClick={() => move(idx, 1)} sx={{ color: 'rgba(255,255,255,0.5)' }}>
                    <ArrowDownwardRoundedIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" disabled={busy}
                    onClick={() => setEditing({
                      id: ex.id,
                      form: {
                        name: ex.name || '',
                        target_sets: ex.target_sets ?? '',
                        target_reps: ex.target_reps ?? '',
                        target_weight: ex.target_weight ?? '',
                        target_rpe: ex.target_rpe ?? '',
                        target_distance: ex.target_distance ?? '',
                        target_duration_sec: ex.target_duration_sec ?? '',
                        rest_seconds: ex.rest_seconds ?? '',
                        notes: ex.notes || '',
                      },
                    })}
                    sx={{ color: '#5b8cff' }}>
                    <EditRoundedIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" disabled={busy}
                    onClick={() => removeExercise(ex.id)} sx={{ color: '#ff6b6b' }}>
                    <DeleteOutlineRoundedIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Box>
            ))}
          </Stack>
        )}

        {/* Palette spécialisée du sport */}
        {library.length > 0 && (
          <Box>
            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 13, mb: 1 }}>
              Ajouter depuis la palette {session.sport_name ? `« ${session.sport_name} »` : ''}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {library.map((tpl) => (
                <Chip
                  key={tpl.name}
                  label={tpl.name}
                  onClick={() => addFromLibrary(tpl)}
                  disabled={busy}
                  clickable
                  size="small"
                  icon={<AddRoundedIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    bgcolor: `${PERF_ACCENT}18`,
                    color: '#fff',
                    border: `1px solid ${PERF_ACCENT}44`,
                    '& .MuiChip-icon': { color: PERF_ACCENT },
                  }}
                />
              ))}
            </Stack>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} sx={{ color: 'rgba(255,255,255,0.7)' }}>Fermer</Button>
      </DialogActions>

      {/* Sous-dialogue : édition/création d'un exercice */}
      <Dialog open={!!editing} onClose={() => setEditing(null)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { bgcolor: '#11162a', color: '#fff' } }}>
        <DialogTitle>{editing && editing.id ? 'Modifier l’exercice' : 'Nouvel exercice'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Nom de l'exercice"
              value={editing ? editing.form.name : ''}
              onChange={(e) => setEditing((s) => ({ ...s, form: { ...s.form, name: e.target.value } }))}
              fullWidth
              sx={darkFieldSx}
            />
            <Grid container spacing={1.5}>
              {NUM_FIELDS.map(([key, label]) => (
                <Grid item xs={6} sm={4} key={key}>
                  <TextField
                    type="number"
                    label={label}
                    value={editing ? editing.form[key] : ''}
                    onChange={(e) =>
                      setEditing((s) => ({ ...s, form: { ...s.form, [key]: e.target.value } }))
                    }
                    fullWidth
                    sx={darkFieldSx}
                  />
                </Grid>
              ))}
            </Grid>
            <TextField
              label="Notes"
              value={editing ? editing.form.notes : ''}
              onChange={(e) => setEditing((s) => ({ ...s, form: { ...s.form, notes: e.target.value } }))}
              fullWidth
              multiline
              minRows={2}
              sx={darkFieldSx}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)} sx={{ color: 'rgba(255,255,255,0.7)' }}>
            Annuler
          </Button>
          <Button
            onClick={saveExercise}
            disabled={busy || !editing || !editing.form.name.trim()}
            variant="contained"
            sx={{ bgcolor: PERF_ACCENT, color: '#0b0f1f' }}
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  )
}
